import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Currency = 'USD' | 'GBP' | 'EUR' | 'CAD' | 'AUD' | 'JPY' | 'CHF';

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: '$',
  GBP: '£',
  EUR: '€',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥',
  CHF: 'Fr'
};

// Cache duration for exchange rates (1 hour)
const EXCHANGE_RATE_CACHE_DURATION = 60 * 60 * 1000;

interface ExchangeRateCache {
  rates: Record<Currency, number>;
  timestamp: number;
}

interface CurrencyStore {
  currency: Currency;
  exchangeRates: Record<Currency, number>;
  lastUpdated: number | null;
  setCurrency: (currency: Currency) => void;
  updateExchangeRates: () => Promise<void>;
}

// Fallback rates in case API is unavailable
const FALLBACK_RATES: Record<Currency, number> = {
  USD: 1,
  GBP: 0.79,
  EUR: 0.96,
  CAD: 1.44,
  AUD: 1.61,
  JPY: 150.45,
  CHF: 0.90
};

export const useCurrencyStore = create<CurrencyStore>()(
  persist(
    (set, get) => ({
      currency: 'USD',
      exchangeRates: FALLBACK_RATES,
      lastUpdated: null,
      setCurrency: (currency) => set({ currency }),
      updateExchangeRates: async () => {
        const lastUpdated = get().lastUpdated;
        const now = Date.now();

        // Only update if cache is expired or doesn't exist
        if (!lastUpdated || now - lastUpdated > EXCHANGE_RATE_CACHE_DURATION) {
          // We'll use this to track our final rates from any source
          let finalRates: Record<Currency, number> = { ...FALLBACK_RATES };
          let ratesSource = 'fallback';
          let allCurrenciesFound = false;

          try {
            // Try the first API endpoint (exchangerate-api.com)
            console.log('Trying primary API: exchangerate-api.com');
            const response1 = await fetch('https://api.exchangerate-api.com/v4/latest/USD', {
              headers: { 'Accept': 'application/json' },
              cache: 'no-cache'
            });
            
            if (response1.ok) {
              const data1 = await response1.json();
              console.log('Primary API response:', data1);
              
              if (data1 && data1.rates) {
                // Create a rates object with proper fallbacks
                const api1Rates: Record<Currency, number> = {
                  USD: 1,
                  GBP: typeof data1.rates.GBP === 'number' ? data1.rates.GBP : FALLBACK_RATES.GBP,
                  EUR: typeof data1.rates.EUR === 'number' ? data1.rates.EUR : FALLBACK_RATES.EUR,
                  CAD: typeof data1.rates.CAD === 'number' ? data1.rates.CAD : FALLBACK_RATES.CAD,
                  AUD: typeof data1.rates.AUD === 'number' ? data1.rates.AUD : FALLBACK_RATES.AUD,
                  JPY: typeof data1.rates.JPY === 'number' ? data1.rates.JPY : FALLBACK_RATES.JPY,
                  CHF: typeof data1.rates.CHF === 'number' ? data1.rates.CHF : FALLBACK_RATES.CHF
                };
                
                // Check if all rates are found
                const missingRates = Object.entries(api1Rates)
                  .filter(([currency, rate]) => rate === FALLBACK_RATES[currency as Currency] && currency !== 'USD')
                  .map(([currency]) => currency);
                
                if (missingRates.length === 0) {
                  // All rates found, we can use these rates
                  finalRates = api1Rates;
                  ratesSource = 'primary API';
                  allCurrenciesFound = true;
                  console.log('All rates found from primary API');
                } else {
                  // Some rates missing, but update what we found
                  console.log(`Missing rates from primary API: ${missingRates.join(', ')}`);
                  for (const currency of Object.keys(api1Rates) as Currency[]) {
                    if (api1Rates[currency] !== FALLBACK_RATES[currency]) {
                      finalRates[currency] = api1Rates[currency];
                    }
                  }
                }
              }
            } else {
              console.log('Primary API returned error status:', response1.status);
            }
          } catch (error1) {
            console.error('Error with primary API:', error1);
          }

          // If not all currencies were found, try second API
          if (!allCurrenciesFound) {
            try {
              // Try the second API endpoint (open.er-api.com)
              console.log('Trying secondary API: open.er-api.com');
              const response2 = await fetch('https://open.er-api.com/v6/latest/USD', {
                headers: { 'Accept': 'application/json' },
                cache: 'no-cache'
              });
              
              if (response2.ok) {
                const data2 = await response2.json();
                console.log('Secondary API response:', data2);
                
                if (data2 && data2.rates) {
                  // Get missing rates from second API
                  for (const currency of Object.keys(FALLBACK_RATES) as Currency[]) {
                    if (finalRates[currency] === FALLBACK_RATES[currency] && currency !== 'USD') {
                      if (typeof data2.rates[currency] === 'number') {
                        finalRates[currency] = data2.rates[currency];
                        console.log(`Found rate for ${currency} from secondary API: ${data2.rates[currency]}`);
                      }
                    }
                  }
                  
                  // Check if all rates are found now
                  const stillMissingRates = Object.entries(finalRates)
                    .filter(([currency, rate]) => rate === FALLBACK_RATES[currency as Currency] && currency !== 'USD')
                    .map(([currency]) => currency);
                  
                  if (stillMissingRates.length === 0) {
                    ratesSource = 'primary and secondary APIs';
                    allCurrenciesFound = true;
                    console.log('All rates found using both APIs');
                  } else {
                    console.log(`Still missing rates: ${stillMissingRates.join(', ')}`);
                  }
                }
              } else {
                console.log('Secondary API returned error status:', response2.status);
              }
            } catch (error2) {
              console.error('Error with secondary API:', error2);
            }
          }

          // If still not all currencies found, try third API
          if (!allCurrenciesFound) {
            try {
              // Try the third API endpoint (frankfurter.app)
              console.log('Trying tertiary API: frankfurter.app');
              const response3 = await fetch('https://api.frankfurter.app/latest?from=USD', {
                headers: { 'Accept': 'application/json' },
                cache: 'no-cache'
              });
              
              if (response3.ok) {
                const data3 = await response3.json();
                console.log('Tertiary API response:', data3);
                
                if (data3 && data3.rates) {
                  // Get missing rates from third API
                  for (const currency of Object.keys(FALLBACK_RATES) as Currency[]) {
                    if (finalRates[currency] === FALLBACK_RATES[currency] && currency !== 'USD') {
                      if (typeof data3.rates[currency] === 'number') {
                        finalRates[currency] = data3.rates[currency];
                        console.log(`Found rate for ${currency} from tertiary API: ${data3.rates[currency]}`);
                      }
                    }
                  }
                }
              } else {
                console.log('Tertiary API returned error status:', response3.status);
              }
            } catch (error3) {
              console.error('Error with tertiary API:', error3);
            }
          }
          
          // Check if we still have any fallback rates
          const finalMissingRates = Object.entries(finalRates)
            .filter(([currency, rate]) => rate === FALLBACK_RATES[currency as Currency] && currency !== 'USD')
            .map(([currency]) => currency);
          
          if (finalMissingRates.length > 0) {
            console.log(`Using fallback rates for: ${finalMissingRates.join(', ')}`);
          }
          
          // Finally, try to use cached rates for any still missing currencies
          try {
            const cachedRatesStr = localStorage.getItem('exchange-rates');
            if (cachedRatesStr) {
              const cachedRates = JSON.parse(cachedRatesStr) as ExchangeRateCache;
              
              // Use cached rates only for currencies that are still using fallbacks
              for (const currency of Object.keys(finalRates) as Currency[]) {
                if (finalRates[currency] === FALLBACK_RATES[currency] && currency !== 'USD') {
                  if (cachedRates.rates[currency] && cachedRates.rates[currency] !== FALLBACK_RATES[currency]) {
                    finalRates[currency] = cachedRates.rates[currency];
                    console.log(`Using cached rate for ${currency}: ${cachedRates.rates[currency]}`);
                  }
                }
              }
            }
          } catch (cacheError) {
            console.error('Error reading cached rates:', cacheError);
          }
          
          console.log(`Final exchange rates (source: ${ratesSource}):`, finalRates);
          
          // Update the store with our final rates
          set({
            exchangeRates: finalRates,
            lastUpdated: now
          });
          
          // Cache the rates for future use
          try {
            localStorage.setItem('exchange-rates', JSON.stringify({
              rates: finalRates,
              timestamp: now
            }));
          } catch (storageError) {
            console.error('Error storing rates in localStorage:', storageError);
          }
        } else {
          console.log('Using cached exchange rates, still valid');
        }
      }
    }),
    {
      name: 'currency-store',
      partialize: (state) => ({
        currency: state.currency,
        exchangeRates: state.exchangeRates,
        lastUpdated: state.lastUpdated
      })
    }
  )
);